import { useEffect, useMemo, useState } from 'react';
import { deepMerge } from '../utils/deepMerge';
import { getPageOverride } from '../services/contentService';

export function useManagedContent({ pageKey, language, defaultTranslations }) {
  const defaultContent = useMemo(
    () => defaultTranslations[language] || defaultTranslations.en || {},
    [defaultTranslations, language]
  );
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    let active = true;

    async function loadOverride() {
      try {
        const override = await getPageOverride(pageKey, language);
        if (!active) {
          return;
        }
        if (!override) {
          setContent(defaultContent);
          return;
        }
        setContent(deepMerge(defaultContent, override));
      } catch {
        // Fall back to local content if Supabase table or query is unavailable.
        if (active) {
          setContent(defaultContent);
        }
      }
    }

    loadOverride();
    const intervalId = window.setInterval(loadOverride, 10000);
    window.addEventListener('focus', loadOverride);
    document.addEventListener('visibilitychange', loadOverride);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', loadOverride);
      document.removeEventListener('visibilitychange', loadOverride);
    };
  }, [defaultContent, pageKey, language]);

  return content;
}
